import type { ComponentProps, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ControlSize = 'sm' | 'md' | 'lg';

// ComponentProps rather than ButtonHTMLAttributes so `ref` is accepted and passed
// through with the rest — a menu that returns focus to its trigger needs the node.
interface ButtonProps extends Omit<ComponentProps<'button'>, 'className'> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Square, for a toolbar icon. Requires `aria-label`: there is no text to read. */
  iconOnly?: boolean;
  /** Fills its container — for the single action at the end of a form. */
  fullWidth?: boolean;
  children?: ReactNode;
}

/**
 * The app's button.
 *
 * `type="button"` by default, not the HTML default of `submit`: a button inside a
 * form that was only meant to open a panel should not post the form, and that
 * mistake is silent until someone loses what they typed.
 */
export const Button = ({
  variant = 'secondary',
  size = 'md',
  iconOnly = false,
  fullWidth = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    className={cx(
      styles.button,
      styles[variant],
      styles[size],
      iconOnly && styles.iconOnly,
      fullWidth && styles.fullWidth,
    )}
    {...rest}
  >
    {children}
  </button>
);
